<template>
  <v-row>
    <v-dialog
      v-model="addUploadDialog"
      persistent
      max-width="800px"
      height="800px"
    >
      <v-card>
        <v-toolbar color="#EEF2F6" max-height="64px">
          <v-card-title>
            <span class="headline">Upload Your Content</span>
          </v-card-title>
          <v-spacer />
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm mr-2"
                v-bind="attrs"
                v-on="on"
                readonly=""
                @click="toggleHelperDialog"
              >
                <i class="fas fa-question" />
              </button>
            </template>
            <span>You can easily upload your video or text content.</span>
          </v-tooltip>
          <button
            :disabled="isLoading"
            type="button"
            class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
            @click="closeAndReset"
          >
            <i class="fas fa-times" />
          </button>
        </v-toolbar>
        <v-container>
          <v-row class="mx-8">
            <v-row>
              <v-col>
                <v-text-field
                  :disabled="isLoading"
                  :loading="isLoading"
                  v-model="unit.name"
                  label="Unit Name"
                  name="name"
                  v-validate="'required'"
                ></v-text-field>
                <span
                  class="kt-font-danger validate-error"
                  v-show="errors.has('name')"
                  autocomplete="off"
                >
                  {{ errors.first('name') }}
                </span>
              </v-col>
              <v-col>
                <v-switch
                  class="float-right"
                  :disabled="isVideoUploaded"
                  v-model="unit.textMode"
                  label="Switch to text mode"
                ></v-switch>
              </v-col>
            </v-row>
          </v-row>
          <div class="mx-8" v-if="unit.textMode == false">
            <template v-if="!unit.youtube_url">
              <dashboard
                :uppy="uppy"
                :props="{
                  proudlyDisplayPoweredByUppy: false,
                  metaFields: [
                    { id: 'name', name: 'Name', placeholder: 'File name' },
                  ],
                  showProgressDetails: true,
                  hidePauseResumeButton: true,
                  hideCancelButton: isLoading,
                  hideProgressAfterFinish: true,
                  hideUploadButton: true,
                  doneButtonHandler: null,
                  height: 300,
                  allowedFileTypes: ['video/*'],
                }"
              />
              <progress-bar
                :uppy="uppy"
                :props="{
                  hideAfterFinish: false,
                }"
              />
              <small>Maximum allowed size is 100mb per upload</small>
              <v-divider />
            </template>
            <v-text-field
              @input="fetchVideoId"
              type="url"
              :disabled="isLoading || isVideoUploaded"
              :loading="isLoading"
              v-model="unit.youtube_url"
              label="Youtube or Vimeo URL"
              name="youtube_url"
              v-if="!fileUpload"
            ></v-text-field>
          </div>
          <div class="mx-8 mb-10" v-else>
            <v-container>
              <v-file-input
                v-model="unit.content_file"
                label="Upload PDF File"
                name="thumbnail"
                accept="application/pdf,"
              ></v-file-input>
              <div class="form-group">
                <jodit-vue v-model="unit.content"></jodit-vue>
              </div>
            </v-container>
          </div>

          <v-btn
            v-if="false"
            class="mx-8 mb-2"
            color="primary"
            :disabled="isLoading"
            @click="toggleShowAdvancedSettings"
            small
          >
            {{ showAdvancedSettings ? 'Hide' : 'Show' }} Advanced Settings
          </v-btn>
          <v-row class="mx-8" v-if="showAdvancedSettings">
            <v-col cols="12" sm="12" md="12">
              <label>Quiz</label>
              <multiselect
                v-model="unit.quiz_id"
                :options="quizzes"
                label="title"
                track-by="id"
                searchable
                @search-change="searchQuizzes"
                placeholder="Select a Quiz"
                :loading="quizPagination.loading"
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  <strong>{{ option.title }}</strong>
                </template>
                <span slot="noOptions">Search Quiz...</span>
                <span slot="noResult">
                  Oops! No elements found. Consider changing the search query.
                </span>
                <template slot="afterList">
                  <div
                    v-observe-visibility="nextQuizPage"
                    v-if="quizPagination.current !== quizPagination.last_page"
                  />
                </template>
              </multiselect>
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <label>Description</label>
              <jodit-vue
                name="description"
                v-model="unit.description"
                :buttons="[]"
                :config="{
                  uploader: { url: 'none' },
                }"
              >
              </jodit-vue>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('description')"
                >{{ errors.first('description') }}</span
              >
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <v-combobox
                v-model="unit.tags"
                label="Tags"
                name="tags"
                :items="tags"
                :item-text="(item) => item.name"
                :item-value="(item) => item.id"
                multiple
                chips
                prepend-icon="fal fa-tags"
                :menu-props="{ closeOnClick: true }"
              ></v-combobox>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('tags')"
                >{{ errors.first('tags') }}</span
              >
            </v-col>
          </v-row>

          <div class="mx-8 mb-10">
            <v-switch
              v-model="unit.is_draft"
              :label="`Draft`"
              :disabled="isLoading"
              :isLoading="isLoading"
            ></v-switch>
          </div>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="blue-grey darken-1"
              text
              :disabled="isLoading"
              :loading="isLoading"
              @click="closeAndReset"
              class="mr-3 px-6"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              class="px-6 mr-3"
              :disabled="isLoading"
              :loading="isLoading"
              @click="publish"
              >Save</v-btn
            >
            <!-- <v-btn
              color="primary"
              class="px-9"
              :disabled="isLoading"
              :loading="isLoading"
              @click="next"
              >Next</v-btn
            > -->
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>

    <v-dialog v-model="publishDialog" max-width="500px">
      <v-card>
        <v-toolbar elevation="0" class="d-flex justify-end">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="btn btn-dark btn-elevate-hover btn-circle btn-icon btn-sm mr-2"
                v-bind="attrs"
                v-on="on"
                dark
              >
                <i class="fas fa-question" />
              </button>
            </template>
            <span
              >You have an option to assign/share to user or add your content to
              a course.</span
            >
          </v-tooltip>
          <button
            type="button"
            class="btn btn-dark btn-elevate-hover btn-circle btn-icon btn-sm"
            @click="closeAndReset"
          >
            <i class="fas fa-times" />
          </button>
        </v-toolbar>
        <v-container v-if="!seriesId">
          <div class="mb-4 mx-4">
            <v-card-title class="mr-10">
              Do you want to assign or share to users or add to course library?
            </v-card-title>
            <v-row>
              <v-col class="text-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      color="primary"
                      class="btn-unit-type"
                      v-bind="attrs"
                      v-on="on"
                      small
                      @click.stop="toggleAssignShareDialog()"
                    >
                      Assign/share <br />to users
                    </v-btn>
                  </template>
                  <span>You may assign/share the unit to a user or group</span>
                </v-tooltip>
              </v-col>
              <AssignShare
                v-on:save="closeAndReset"
                v-model="assignShareDialog"
                :unit="result"
                :key="key"
              />
              <v-col class="text-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      color="primary"
                      class="btn-unit-type"
                      v-bind="attrs"
                      v-on="on"
                      small
                      @click="toggleAddToLibraryDialog()"
                    >
                      Add to <br />course library
                    </v-btn>
                  </template>
                  <span
                    >You have an option to select where to add the unit to a
                    series</span
                  >
                </v-tooltip>
              </v-col>
              <AddToLibrary
                v-on:save="closeAndReset"
                v-model="addToLibraryDialog"
                :unit="result"
                :key="key"
              />
            </v-row>
          </div>
        </v-container>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { Dashboard, DashboardModal, DragDrop, ProgressBar } from '@uppy/vue';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import AssignShare from '@/components/dialogs/AssignShareDialog.vue';
import AddToLibrary from '@/components/dialogs/AddToLibraryDialog.vue';
import HelperDialog from '@/components/dialogs/HelperDialog.vue';
import {
  debounce as _debounce,
  findIndex as _findIndex,
  forEach as _forEach,
  isEmpty as _isEmpty,
} from 'lodash-es';
import EventBus from '@/plugins/eventBus';

export default {
  name: 'UploadDialog',
  components: {
    Dashboard,
    DashboardModal,
    DragDrop,
    ProgressBar,
    AssignShare,
    AddToLibrary,
  },
  props: {
    isMobile: {
      required: false,
      default: false,
      type: Boolean,
    },
    addUploadDialog: Boolean,
    toggleUploadDialog: Function,
    closeDialog: {
      required: false,
      default: null,
      type: Function,
    },
    courseId: {
      required: false,
      default: null,
      type: Number,
    },
    moduleId: {
      required: false,
      default: null,
      type: Number,
    },
    seriesId: {
      required: false,
      default: null,
      type: Number,
    },
    curriculumId: {
      required: false,
      default: null,
      type: Number,
    },
  },
  data() {
    return {
      showAdvancedSettings: false,
      fileUpload: null,
      isLoading: false,
      uploadDialog: false,
      publishDialog: false,
      switchToText: false,
      assignShareDialog: false,
      addToLibraryDialog: false,
      //  VIDEOS
      isVideoUploaded: false,
      youtube_url: null,
      youtube_id: null,
      urlSource: null,
      debounce: null,
      video: {},
      isUploading: false,
      xhrUploadHost: null,
      unit: {
        description: '',
        id: null,
        name: null,
        thumbnail: [],
        content: '',
        youtube_url: null,
        youtube_id: null,
        urlSource: null,
        file: null,
        textMode: false,
        content_file: null,
        is_draft: false,
        tags: [],
        quiz_id: null,
      },
      quizzes: [],
      quizPagination: {
        current: 1,
        last_page: 1,
        loading: false,
      },
      draft: false,
      showHelperDialog: false,
      key: 0,
    };
  },
  mounted() {
    this.xhrUploadHost = window.location.origin;
  },
  beforeDestroy() {
    this.uppy.close();
  },
  computed: {
    tags() {
      return this.$store.state.units.tags;
    },
    uppy() {
      let vm = this;
      return new Uppy({
        id: 'uppyMain',
        debug: true,
        allowMultipleUploads: false,
        autoProceed: false,
        restrictions: {
          allowedFileTypes: ['video/*'],
          maxFileSize: 100 * 1024 * 1024,
          maxNumberOfFiles: 1,
        },
      })
        .on('file-added', (file) => {
          this.isLoading = true;
          this.addFile(file.data);
        })
        .on('file-removed', (file) => {
          this.isLoading = true;
          this.removeFile();
        })
        .on('cancel-all', () => {
          this.isLoading = true;
          this.removeFile();
        });
    },
    showResults() {},
    result() {
      return this.unit;
    },
  },
  methods: {
    forceReRender() {
      this.key += 1;
    },
    toggleShowAdvancedSettings() {
      this.showAdvancedSettings = !this.showAdvancedSettings;
    },
    addFile(file) {
      this.fileUpload = file;
      this.isLoading = false;
    },
    removeFile() {
      this.fileUpload = null;
      this.isLoading = false;
    },
    //function for publish
    publish() {
      this.isLoading = true;
      const textData = new FormData();
      textData.append('name', this.unit.name);
      textData.append('content', this.unit.content);
      textData.append('file', this.unit.content_file);
      textData.append('textMode', this.unit.textMode);
      textData.append('is_draft', this.unit.is_draft);
      textData.append('description', this.unit.description);
      textData.append('series_id', this.seriesId);
      textData.append('module_id', this.moduleId);
      textData.append('course_id', this.courseId);
      textData.append('curriculum_id', this.curriculumId);
      _forEach(this.unit.tags, (tag) => {
        typeof tag === 'object'
          ? textData.append('tags[]', tag.id)
          : textData.append('tags[]', tag);
      });
      if (this.unit.quiz_id) {
        textData.append('quiz_id', this.unit.quiz_id.id);
      }

      const videoData = new FormData();
      videoData.append('name', this.unit.name);
      videoData.append('is_draft', this.unit.is_draft);
      videoData.append('files', this.fileUpload);
      videoData.append('youtube_url', this.unit.youtube_url);
      videoData.append('youtube_id', this.unit.youtube_id);
      videoData.append('urlSource', this.unit.urlSource);
      videoData.append('description', this.unit.description);
      videoData.append('series_id', this.seriesId);
      videoData.append('module_id', this.moduleId);
      videoData.append('course_id', this.courseId);
      videoData.append('curriculum_id', this.curriculumId);
      _forEach(this.unit.tags, (tag) => {
        typeof tag === 'object'
          ? videoData.append('tags[]', tag.id)
          : videoData.append('tags[]', tag);
      });
      if (this.unit.quiz_id) {
        videoData.append('quiz_id', this.unit.quiz_id.id);
      }

      const payload = this.unit.textMode ? textData : videoData;
      this.$validator.validate('*').then((result) => {
        if (result) {
          this.isLoading = true;
          this.$http
            .post('client/lms/publish/unit', payload)
            .then((response) => {
              this.$router.push({
                name: 'EditUnitPage',
                params: {
                  id: response.data.unit.id,
                },
              });
              this.unit = response.data.unit;
              EventBus.$emit('RELOAD_TABLE');
              this.$emit('unit', response.data.seriesUnit);
              console.log('unit', response.data);
            })
            .catch((response) => {
              console.log(response);
            })
            .finally((response) => {
              this.close();
              this.publishDialog = false;
              // this.$notify('success', 'Draft unit saved!');
              this.isLoading = false;
            });
        }
      });
      this.isLoading = false;
    },
    toggleAssignShareDialog() {
      this.forceReRender();
      this.assignShareDialog = true;
      this.publishDialog = false;
    },
    toggleAddToLibraryDialog() {
      this.forceReRender();
      this.addToLibraryDialog = true;
      this.publishDialog = false;
    },
    reset() {
      this.errors.clear();
      this.$validator.reset();
      (this.unit = {
        description: '',
        id: null,
        name: null,
        thumbnail: [],
        content: '',
        youtube_url: null,
        youtube_id: null,
        urlSource: null,
        file: null,
        textMode: false,
        content_file: null,
        is_draft: false,
        tags: [],
        quiz_id: null,
      }),
        (this.youtube_url = null);
    },
    closeAndReset() {
      this.reset();
      this.close();
      this.closeDialog();
    },
    close() {
      this.publishDialog = false;
      this.uploadDialog = false;
      this.showAdvancedSettings = false;
      this.addUploadDialog = false;
    },
    toggleHelperDialog() {
      this.showHelperDialog = true;
    },
    closeConfirmDialog() {
      this.showHelperDialog = false;
    },
    youtubeParser() {
      const regExp =
        /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
      const match = this.unit.youtube_url.match(regExp);
      const youtubeId = match && match[7].length == 11 ? match[7] : false;
      console.log(youtubeId);
      if (youtubeId) {
        this.unit.youtube_id =
          match && match[7].length == 11 ? match[7] : false;
        this.unit.urlSource = 'youtube';
      } else {
        this.$notify('warning', 'Not a Youtube or Vimeo URL');
        this.unit.youtube_url = null;
        this.unit.youtube_id = null;
      }
    },
    getIdFromVimeoURL(url) {
      const vUrl =
        /(https?:\/\/)?(www\.)?(player\.)?vimeo\.com\/([a-z]*\/)*([0-9]{6,11})[?]?.*/.exec(
          url
        );
      return vUrl[vUrl.length - 1];
    },

    fetchVideoId(url) {
      if (this.debounce !== null) clearTimeout(this.debounce);
      const vm = this;
      this.debounce = setTimeout(function () {
        vm.loading = true;
        if (url.includes('vimeo')) {
          vm.unit.youtube_id = vm.getIdFromVimeoURL(url);
          vm.unit.urlSource = 'vimeo';
        } else {
          vm.youtubeParser();
        }
        vm.loading = false;
      }, 500);
    },
    getCookie(cname) {
      const name = cname + '=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length);
        }
      }
      return null;
    },

    fetchQuizzes() {
      this.quizPagination.loading = true;
      const page = _isEmpty(this.search) ? this.quizPagination.current : 1;
      this.axios
        .get('lms-manager/builder/quizzes', {
          params: {
            page,
            type: 'Active',
            itemsPerPage: 5,
            search: this.search,
          },
        })
        .then(({ data }) => {
          _forEach(data.data, (quiz) => {
            const index = _findIndex(this.quizzes, { id: quiz.id });
            if (index === -1) {
              this.quizzes.push(quiz);
            }
          });
          if (_isEmpty(this.search)) {
            this.quizPagination.last_page = data.last_page;
          }
          this.quizPagination.loading = false;
        })
        .catch((error) => {
          this.quizPagination.loading = false;
        });
    },

    searchQuizzes: _debounce(function (query) {
      this.search = query;
      this.fetchQuizzes();
    }, 500),
  },
  watch: {},
};
</script>

<style scoped>
a.uppy-Dashboard-poweredBy {
  display: none !important;
}
/* .btn-unit-type {
  margin: 10% 35px 10% 35px;
  border: 0.7px dashed;
  border-color: rgb(249, 180, 24);
} */
.btn-unit-type {
  padding: 30px 40px !important;
}

.pulsing,
.pulsing-active {
  cursor: pointer;
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
  animation: pulse-black 1.2s infinite;
}
/* Animation */
@keyframes pulse-black {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(0, 0, 0 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0 0);
  }
}
</style>
<style src="@uppy/core/dist/style.css"></style>
<style src="@uppy/dashboard/dist/style.css"></style>
<style src="@uppy/drag-drop/dist/style.css"></style>
<style src="@uppy/progress-bar/dist/style.css"></style>
